import type { JSX } from 'preact';
import { useEffect, useRef, useContext } from 'preact/compat';
import { useModal } from './hooks/useModal';
import { Layer } from '../UNSAFE_Layer';
import { LogicalParentRefContext } from '../UNSAFE_Layer/LogicalParentRefContext';
import ModalBackdrop from './ModalBackdrop';
import ModalManager from './ModalManager';
import { baseStyle } from './themes/ModalStyles.css';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';

// Global ModalManager singleton instance
const globalModalManager = new ModalManager();

/**
 * Returns an array of element's siblings.
 *
 * @param elem
 * @returns
 */
function getSiblings(elem: Element): Element[] {
  const siblings: Element[] = [];
  let sibling = elem.previousSibling;
  while (sibling != null) {
    if (sibling.nodeType === 1) {
      siblings.push(sibling as Element);
    }
    sibling = sibling.previousSibling;
  }
  sibling = elem.nextSibling;
  while (sibling != null) {
    if (sibling.nodeType === 1) {
      siblings.push(sibling as Element);
    }
    sibling = sibling.nextSibling;
  }
  return siblings;
}

/**
 * Sets aria-hidden=true on Modal ancestors and siblings.
 *
 * @param modal
 * @returns An array of modified elements.
 */
function setAriaHiddenOnBackgroundElements(modal: HTMLElement) {
  if (!modal) {
    return [];
  }
  const ariaHiddenElements: Element[] = [];
  let node: Element | null = modal.parentElement as Element;

  do {
    if (!node) {
      break;
    }
    const siblings = getSiblings(node);
    siblings.forEach((elem: Element) => {
      if (elem.tagName.toLowerCase() !== 'script' && elem.getAttribute('aria-hidden') !== 'true') {
        ariaHiddenElements.push(elem);
        elem.setAttribute('aria-hidden', 'true');
      }
    });
    node = node.parentElement;
  } while (node && node.tagName.toLowerCase() !== 'body');

  return ariaHiddenElements;
}

/**
 * Resets the aria-hidden state on previously aria-hidden elements.
 *
 * @param elements
 */
function resetAriaHiddenOnBackgroundElements(elements: Element[]) {
  elements.forEach((elem) => {
    elem.removeAttribute('aria-hidden');
  });
}

type IntrinsicProps = Pick<UnpackSignals<JSX.IntrinsicElements['div']>, 'children'>;
type UseModalProps = Parameters<typeof useModal>[0];
type Props = UseModalProps &
  IntrinsicProps & {
    /**
     * Determines if the modal is active.
     */
    isOpen: boolean;
    /**
     * Specifies if modal backdrop is visible or transparent. Default is 'scrim' (visible).
     */
    backdropVariant?: 'scrim' | 'transparent';
    /**
     * A callback to be invoked when a click on the modal backdrop occurs.
     */
    onBackdropClick?: (event: MouseEvent) => void;
  };

/**
 * Modal is a low-level component that provides the 'modality' feature with built-in overlay (scrim).
 * It is typically used for building higher-level components (such as 'Dialog' or 'Popup') that need
 * to support modal behavior preventing interaction with the rest of the page while the modal is open.
 */
const Modal = ({ children, isOpen, onBackdropClick, backdropVariant }: Props) => {
  // get the global ModalContext instance
  const modalManager = globalModalManager;

  const { backdropRef } = useModal({ isOpen, onBackdropClick });
  // main modal element ref
  const modalRef = useRef<HTMLDivElement>(null);

  const ariaHiddenCache = useRef<Element[] | null>(null);

  // modal ref cache, required to lookup the item in modal manager when the actual ref is unmounted
  const modal = useRef<{
    modalRef?: typeof modalRef.current;
  }>({});

  // populates and returns the modal ref cache
  const getModal = () => {
    modal.current.modalRef = modalRef.current;
    return modal.current;
  };

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalManager?.push(getModal());
    } else {
      if (modal.current.modalRef) {
        if (ariaHiddenCache.current !== null) {
          resetAriaHiddenOnBackgroundElements(ariaHiddenCache.current);
          ariaHiddenCache.current = null;
        }
        modalManager?.pop(getModal());
      }
    }
  }, [isOpen, modalManager]);

  useEffect(() => {
    const modalRef = modal.current.modalRef;
    return () => {
      if (ariaHiddenCache.current !== null) {
        resetAriaHiddenOnBackgroundElements(ariaHiddenCache.current);
        ariaHiddenCache.current = null;
      }
      if (modalRef) {
        modalManager?.pop(getModal());
      }
    };
  }, [modalManager]);

  const handleFocusIn = () => {
    // on first focusin, set aria-hidden on modal ancestor's siblings and remember the modified elements
    if (modalRef.current && ariaHiddenCache.current === null) {
      ariaHiddenCache.current = setAriaHiddenOnBackgroundElements(modalRef.current);
    }
  };

  const logicalParentRef = useContext(LogicalParentRefContext);

  return isOpen ? (
    <Layer isModal={true} logicalParentRef={logicalParentRef}>
      <div ref={modalRef} class={baseStyle} onFocus={handleFocusIn}>
        <ModalBackdrop ref={backdropRef} variant={backdropVariant} />
        {children}
      </div>
    </Layer>
  ) : null;
};

export default Modal;
